import React from 'react'
import GD from '../GD'
import styled from 'styled-components'
import { Grid } from '../components/Grid'

/**
 * First row explanation:
 * 1 - images amount, 0 - image index, 12 - image width for Grid component (6 - half width, 12 - full width)
 * */
const widthForAmount = {
  1: { 0: 12 },
  2: { 0: 6, 1: 6 },
  3: { 0: 12, 1: 6, 2: 6 },
  4: { 0: 6, 1: 6, 2: 6, 3: 6 },
  5: { 0: 12, 1: 6, 2: 6, 3: 6, 4: 6 },
  6: { 0: 12, 1: 6, 2: 6, 3: 6, 4: 6, 5: 12 },
  7: { 0: 12, 1: 6, 2: 6, 3: 6, 4: 6, 5: 6, 6: 6 },
  8: { 0: 12, 1: 6, 2: 6, 3: 6, 4: 6, 5: 6, 6: 6, 7: 12 },
  9: { 0: 12, 1: 6, 2: 6, 3: 6, 4: 6, 5: 6, 6: 6, 7: 6, 8: 6 },
  10: { 0: 12, 1: 6, 2: 6, 3: 6, 4: 6, 5: 12, 6: 6, 7: 6, 8: 6, 9: 6 },
}

const MessageImageDiv = styled.div`
  background-color: rgba(0, 0, 0, 0.15);
  width: 100%;
  height: 125px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background-size: contain;
  }
`

const MessageImageGroup = React.memo((params: { msg: IMessage }) => {
  const imagesRef = React.useRef<HTMLDivElement[]>([])
  const [b64, setB64] = React.useState<Record<string, string>>({})

  const imgData = React.useMemo(() => {
    return params.msg.imageGroup?.map((el: IMessage) => (el.sys?.additionalData as string).split(','))
  }, [params.msg.imageGroup])
  const imgUIDs: string[] = React.useMemo(() => {
    return imgData?.map(el => el[0]) || []
  }, [imgData])

  const images: IMessage[] | undefined = params.msg.imageGroup
  const count: number = images?.length || 0

  React.useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(async (entry) => {
        if (entry.isIntersecting) {
          const imgUID = entry.target.getAttribute('data-uid') as string

          const base64: string | null = await GD.FETCH_IMAGE.request({ thumb: true, imageUID: imgUID })

          if (base64) setB64(state => ({ ...state, [imgUID]: base64 }))

          observer.unobserve(entry.target)
        }
      })
    })

    imagesRef.current.forEach(el => observer.observe(el)) // Start observing

    return () => {
      observer.disconnect()

      setB64({})
    }
  }, [params.msg.chatUID])

  const onImageClick = (e: React.MouseEvent, img: IMessage) => {
    e.stopPropagation()
    if (params.msg.sys && params.msg.sys.additionalData) {
      const [uid, width, height] = (img.sys?.additionalData as string).split(',')

      GD.DOWNLOAD_IMAGE.invoke({
        target: 'msg',
        src: e.target as HTMLDivElement,
        uid: uid,
        name: img.sys?.title as string,
        chatUID: img.chatUID,
        width: width,
        height: height,
      })
    }
  }

  return (
    <Grid fullwidth style={{ flexWrap: 'wrap' }} gap={5}>
      {images?.map((img, idx) => {
        const widthForIndexes = widthForAmount[count]
        const uid = (img.sys?.additionalData as string)?.split(',')?.[0] ?? null
        if (!uid) return null

        return (
          <Grid.Col key={img.id} xs={widthForIndexes?.[idx]}>
            <MessageImageDiv
              ref={el => imagesRef.current[idx] = el as HTMLDivElement}
              onClick={e => onImageClick(e, img)}
              style={{ backgroundImage: b64[uid] ? `url(${b64[uid]})` : 'none' }}
              data-uid={imgUIDs[idx]}
            />
          </Grid.Col>
        )
      })}
    </Grid>
  )
})

export {
  MessageImageGroup,
}